import { AuthResponse } from '@core/models/auth.model';
import { Employment } from '@core/models/common.model';
import { JwtUtils } from './jwt-utils';
import { SessionUtils } from './session-utils';

export class TokenManager {
  static processResponse(
    response: AuthResponse,
    fallbackRefreshToken?: string | null,
  ): ProcessedAuthData {
    const {
      accessToken,
      expirationDate,
      employments,
      tableNumber,
      numberOfParticipants,
      tableSessionPublicId,
    } = response;

    // Resolvemos el refresh token: prioridad respuesta > fallback > 'guest'
    const finalRefreshToken = this.resolveRefreshToken(
      response.refreshToken,
      fallbackRefreshToken || null,
    );

    // Extraemos datos del JWT (foodVenueId, y tableSessionId o vencimiento si no vienen en el cuerpo)
    const tokenData = this.extractSessionDataFromToken(accessToken);

    return {
      accessToken,
      refreshToken: finalRefreshToken,
      expirationDate: expirationDate ?? tokenData.expirationDate,
      // Priorizamos los datos explícitos del JSON de respuesta
      tableSessionId: tableSessionPublicId || tokenData.tableSessionId,
      foodVenueId: tokenData.foodVenueId,
      tableNumber: tableNumber ?? null,
      participantCount: numberOfParticipants || 0,
      employments: employments || [],
    };
  }

  /**
   * Extrae información útil para el resto de la app desde la respuesta
   */
  static getSessionInfoFromResponse(response: AuthResponse) {
    if (response.tableNumber) {
      return {
        tableNumber: response.tableNumber,
        participants: response.activeParticipants,
      };
    }
    return null;
  }

  static validateTokens(accessToken: string, refreshToken: string): void {
    if (!accessToken || !JwtUtils.isValidToken(accessToken)) {
      throw new Error('AccessToken inválido o expirado');
    }
    if (refreshToken !== 'guest' && !refreshToken) {
      throw new Error('RefreshToken inválido');
    }
  }

  static saveTokens(
    accessToken: string,
    refreshToken: string,
    expirationDate?: string | null,
  ): void {
    this.validateTokens(accessToken, refreshToken);

    let finalExpiration = expirationDate;
    if (!finalExpiration) {
      const decoded = JwtUtils.decodeJWT(accessToken);
      if (decoded?.exp) finalExpiration = new Date(decoded.exp * 1000).toISOString();
    }

    SessionUtils.setStorageValue('accessToken', accessToken);
    SessionUtils.setStorageValue('refreshToken', refreshToken);
    if (finalExpiration) SessionUtils.setStorageValue('expirationDate', finalExpiration);
  }

  static saveSessionData(data: SessionData): void {
    SessionUtils.setStorageValue('tableSessionId', data.tableSessionId);
    SessionUtils.setStorageValue('foodVenueId', data.foodVenueId);
    SessionUtils.setStorageValue('tableNumber', data.tableNumber);
    if (data.employments) {
      SessionUtils.setStorageValue('employments', JSON.stringify(data.employments));
    }
  }

  static loadAuthData(): LoadedAuthData {
    const emp = SessionUtils.getCleanStorageValue('employments');
    return {
      accessToken: SessionUtils.getCleanStorageValue('accessToken'),
      refreshToken: SessionUtils.getCleanStorageValue('refreshToken'),
      expirationDate: SessionUtils.getCleanStorageValue('expirationDate'),
      tableSessionId: SessionUtils.getCleanStorageValue('tableSessionId'),
      foodVenueId: SessionUtils.getCleanStorageValue('foodVenueId'),
      employments: emp ? JSON.parse(emp) : null,
    };
  }

  private static extractSessionDataFromToken(accessToken: string) {
    const decoded = JwtUtils.decodeJWT(accessToken);
    return {
      tableSessionId: SessionUtils.cleanSessionValue(decoded?.tableSessionId),
      foodVenueId: SessionUtils.cleanSessionValue(decoded?.foodVenueId),
      expirationDate: decoded?.exp ? new Date(decoded.exp * 1000).toISOString() : null,
    };
  }

  private static resolveRefreshToken(
    respToken: string | null | undefined,
    fallback: string | null,
  ): string {
    if (respToken) return respToken;
    const cleanFallback = SessionUtils.cleanSessionValue(fallback);
    return cleanFallback || 'guest';
  }

  static setVenueContext(id: string): void {
    if (id) SessionUtils.setStorageValue('foodVenueId', id);
  }

  static clearVenueContext(): void {
    SessionUtils.setStorageValue('foodVenueId', null);
  }

  public static getEmploymentsFromResponse(response: AuthResponse): Employment[] {
    return response?.employments || [];
  }
}

// ==================== INTERFACES ====================

export interface ProcessedAuthData {
  accessToken: string;
  refreshToken: string;
  expirationDate: string | null;
  tableSessionId: string | null;
  foodVenueId: string | null;
  tableNumber: number | null;
  participantCount: number | null;
  employments: Employment[];
}

export interface LoadedAuthData {
  accessToken: string | null;
  refreshToken: string | null;
  expirationDate: string | null;
  tableSessionId: string | null;
  foodVenueId: string | null;
  employments: Employment[] | null;
}

export interface SessionData {
  tableSessionId: string | null;
  foodVenueId: string | null;
  tableNumber?: number | null;
  participantCount?: number | null;
  employments?: Employment[] | null;
}
